import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text, Image, Platform, Pressable } from 'react-native';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSequence,
  withSpring,
  withTiming,
  Easing,
  interpolate,
  Extrapolation,
} from 'react-native-reanimated';
import { SvgIcon } from '../../components/common/SvgIcon';
import { Clickable, PRESS_PRESETS } from '../../components/common/Clickable';
import { type ThemeColors } from '../../shared/theme';
import { formatCurrency } from '../../shared/utils/currencyUtils';
import { getApiText, useTranslation, formatNumber } from '../../shared/i18n';
import { useRtl } from '../../shared/i18n/useRtl';
import {
  hasActiveSubscriptionOffer,
  planImageFromCatalogPlanLike,
  resolveCatalogPlanCardNutrition,
  resolveSubscriptionCardNutritionDisplay,
  resolvePlanOfferPricing,
  formatPersonCountLabel,
  type PlanItem,
} from '../../data/domains/subscription';
import {
  useShowSubscriptionScaledMacrosEnabled,
  useCaloriesLimitVisible,
  usePortionTextMode,
  useShowPortionEnabled,
} from '../../data/domains/config';
import { resolveAttachmentUri } from '../../shared/utils/resolveMediaUri';
import { DASH_STUDIO_RADIUS } from '../../shared/theme/studio/dashboardConstants';
import type { SubscriptionListStyles } from './subscriptionListStyles';
import { PLAN_CARD_IMAGE_HEIGHT } from './subscriptionListStyles';

const ANDROID_TEXT = Platform.OS === 'android' ? { includeFontPadding: false } : {};
const OFFER_POP = { stiffness: 260, damping: 18, mass: 0.7 };
const EXPAND_TIMING = { duration: 280, easing: Easing.bezier(0.22, 1, 0.36, 1) };
const MERGED_IMAGE_COLLAPSED = Math.round(PLAN_CARD_IMAGE_HEIGHT * 0.72);
const MERGED_DETAILS_MAX = 132;

type Props = {
  item: PlanItem;
  colors: ThemeColors;
  styles: SubscriptionListStyles;
  onSelect: () => void;
  onViewDetails?: (item: PlanItem) => void;
  /** When true, card shows selected chrome (merged list selection). */
  selected?: boolean;
  /**
   * Merged list only — card starts collapsed; selecting expands details smoothly.
   * Other call sites omit this and keep the previous always-expanded card.
   */
  expandable?: boolean;
};

/** One-shot pop on mount — no perpetual pulse (list-friendly). */
function OfferBadge({ label, styles }: { label: string; styles: SubscriptionListStyles }) {
  const pop = useSharedValue(0.92);

  useEffect(() => {
    pop.value = withSequence(
      withTiming(1.05, { duration: 140, easing: Easing.out(Easing.quad) }),
      withSpring(1, OFFER_POP),
    );
  }, [pop]);

  const animated_style = useAnimatedStyle(() => ({
    transform: [{ scale: pop.value }],
  }));

  return (
    <Animated.View style={[styles.badgeOffer, animated_style]}>
      <Text style={[styles.badgeOfferText, ANDROID_TEXT]}>{label}</Text>
    </Animated.View>
  );
}

function buildCalorieRangeLabel(
  display: ReturnType<typeof resolveSubscriptionCardNutritionDisplay>,
): string | null {
  if (!display.show_calories_limit) {
    return null;
  }
  const min = display.fields.min_calories != null ? formatNumber(Math.round(display.fields.min_calories)) : null;
  const max = display.fields.max_calories != null ? formatNumber(Math.round(display.fields.max_calories)) : null;
  if (min != null && max != null) {
    return `${min} – ${max} kcal`;
  }
  if (min != null) {
    return `${min} kcal`;
  }
  if (max != null) {
    return `${max} kcal`;
  }
  return null;
}

function buildScaledOrPortionLine(
  display: ReturnType<typeof resolveSubscriptionCardNutritionDisplay>,
  t: (key: string, params?: Record<string, string | number>) => string,
  portion_text_mode: 'portion' | 'person',
): string | null {
  if (display.show_portion && display.fields.portion != null) {
    return formatPersonCountLabel(display.fields.portion, t, portion_text_mode);
  }
  if (!display.show_scaled_macros) {
    return null;
  }
  const parts: string[] = [];
  if (display.fields.scaled_protein != null) {
    parts.push(`${formatNumber(Math.round(display.fields.scaled_protein))}g P`);
  }
  if (display.fields.scaled_carbs != null) {
    parts.push(`${formatNumber(Math.round(display.fields.scaled_carbs))}g C`);
  }
  return parts.length > 0 ? parts.join('  ·  ') : null;
}

export const SubscriptionCatalogPlanCard = memo(function SubscriptionCatalogPlanCard({
  item,
  colors,
  styles,
  onSelect,
  onViewDetails,
  selected = false,
  expandable = false,
}: Props) {
  const { t } = useTranslation();
  const rtl = useRtl();
  const { chevronNextAlongAxis, flipRowStyle, textAlign, writingDirection } = rtl;
  const show_scaled_macros_gate = useShowSubscriptionScaledMacrosEnabled();
  const calories_limit_gate = useCaloriesLimitVisible();
  const show_portion_gate = useShowPortionEnabled();
  const portion_text_mode = usePortionTextMode();
  const [image_failed, set_image_failed] = useState(false);
  const expand_progress = useSharedValue(expandable && selected ? 1 : 0);

  useEffect(() => {
    if (!expandable) {
      expand_progress.value = 0;
      return;
    }
    expand_progress.value = withTiming(selected ? 1 : 0, EXPAND_TIMING);
  }, [expandable, selected, expand_progress]);

  const p = item.plan;
  const pricing = useMemo(() => resolvePlanOfferPricing(p as Record<string, unknown>), [p]);
  const price = pricing.displayPrice;
  const strike_price =
    pricing.strikethroughPrice != null && pricing.strikethroughPrice > pricing.displayPrice
      ? pricing.strikethroughPrice
      : null;
  const has_offer = hasActiveSubscriptionOffer(p) || strike_price != null;
  const meals = p.total_meals ?? p.number_of_meals ?? 0;
  const snacks = p.total_snacks ?? p.number_of_snacks ?? 0;
  const plan_fallback = item.categoryTitle === 'Plan' ? t('subscriptions.plan') : item.categoryTitle;
  const title =
    getApiText(p as Record<string, unknown>, 'title', 'arabic_title') || p.title || p.name || `${plan_fallback} · ${item.days}d`;
  const image_uri = useMemo(() => {
    if (image_failed) {
      return null;
    }
    const from_plan = planImageFromCatalogPlanLike(p);
    if (from_plan) {
      return from_plan;
    }
    return resolveAttachmentUri(item.categoryImage);
  }, [p, item.categoryImage, image_failed]);

  const nutrition_display = useMemo(() => {
    const fields = resolveCatalogPlanCardNutrition(p);
    return resolveSubscriptionCardNutritionDisplay(fields, {
      show_scaled_macros: show_scaled_macros_gate,
      calories_limit: calories_limit_gate,
      show_portion: show_portion_gate,
    });
  }, [p, show_scaled_macros_gate, calories_limit_gate, show_portion_gate]);

  const calorie_label = useMemo(() => buildCalorieRangeLabel(nutrition_display), [nutrition_display]);
  const scaled_line = useMemo(
    () => buildScaledOrPortionLine(nutrition_display, t, portion_text_mode),
    [nutrition_display, portion_text_mode, t],
  );
  const show_nutrition = Boolean(scaled_line || calorie_label);

  const meals_label = meals > 0 ? `${formatNumber(meals)} ${t('dashboard.mealsLabel')}` : null;
  const snacks_label = snacks > 0 ? `${formatNumber(snacks)} ${t('dashboard.snacksLabel')}` : null;
  const days_label = `${formatNumber(item.days)} ${t('subscriptions.daysLabel')}`;
  const plain_meta_line = [meals_label, snacks_label, days_label].filter(Boolean).join(' · ');

  const on_image_error = useCallback(() => set_image_failed(true), []);
  const on_view_details = useCallback(() => {
    onViewDetails?.(item);
  }, [onViewDetails, item]);

  const image_block_anim = useAnimatedStyle(() => {
    if (!expandable) {
      return {};
    }
    return {
      height: interpolate(
        expand_progress.value,
        [0, 1],
        [MERGED_IMAGE_COLLAPSED, PLAN_CARD_IMAGE_HEIGHT],
        Extrapolation.CLAMP,
      ),
    };
  }, [expandable, expand_progress]);

  const details_anim = useAnimatedStyle(() => {
    if (!expandable) {
      return {};
    }
    return {
      maxHeight: interpolate(expand_progress.value, [0, 1], [0, MERGED_DETAILS_MAX], Extrapolation.CLAMP),
      opacity: interpolate(expand_progress.value, [0, 0.35, 1], [0, 0, 1], Extrapolation.CLAMP),
      overflow: 'hidden' as const,
    };
  }, [expandable, expand_progress]);

  const compact_meta_anim = useAnimatedStyle(() => {
    if (!expandable) {
      return {};
    }
    return {
      opacity: interpolate(expand_progress.value, [0, 0.45, 1], [1, 0.35, 0], Extrapolation.CLAMP),
    };
  }, [expandable, expand_progress]);

  const render_select_chip = (compact_wrap: boolean) => {
    if (onViewDetails) {
      return (
        <Pressable
          onPress={on_view_details}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel={t('subscriptions.viewDetails')}
          style={styles.planCardViewDetailsWrap}>
          <Text style={styles.planCardViewDetailsText}>{t('subscriptions.viewDetails')}</Text>
        </Pressable>
      );
    }
    return (
      <View
        style={[styles.planCardSelectWrap, compact_wrap && styles.planCardSelectWrapCompact]}
        pointerEvents="none">
        <View style={[styles.planCardSelectInner, flipRowStyle]}>
          <Text style={styles.planCardSelectText}>
            {selected ? t('account.selected') : t('subscriptions.select')}
          </Text>
          <SvgIcon name={selected ? 'check' : chevronNextAlongAxis} size={13} color={colors.onPrimary} />
        </View>
      </View>
    );
  };

  const expanded_details = (
    <>
      {show_nutrition ? (
        <View style={[styles.planCardColumnsRow, flipRowStyle]}>
          <View style={styles.planCardTagsStack}>
            {meals_label || snacks_label ? (
              <View style={[styles.planCardTagsCluster, flipRowStyle]}>
                {meals_label ? (
                  <View
                    style={[
                      styles.planCardMetaTag,
                      styles.planCardMetaTagFlex,
                      { backgroundColor: colors.primary, borderColor: colors.primary },
                    ]}>
                    <Text style={[styles.planCardMetaTagText, { color: colors.primaryText }, ANDROID_TEXT]} numberOfLines={1}>
                      {meals_label}
                    </Text>
                  </View>
                ) : null}
                {snacks_label ? (
                  <View
                    style={[
                      styles.planCardMetaTag,
                      styles.planCardMetaTagFlex,
                      { backgroundColor: colors.primaryText, borderColor: colors.primary },
                    ]}>
                    <Text style={[styles.planCardMetaTagText, { color: colors.primary }, ANDROID_TEXT]} numberOfLines={1}>
                      {snacks_label}
                    </Text>
                  </View>
                ) : null}
              </View>
            ) : null}
            <View
              style={[
                styles.planCardMetaTag,
                styles.planCardDaysTagFull,
                { backgroundColor: colors.tertiary, borderColor: colors.tertiary },
              ]}>
              <Text style={[styles.planCardMetaTagText, { color: colors.tertiaryText }, ANDROID_TEXT]} numberOfLines={1}>
                {days_label}
              </Text>
            </View>
          </View>

          <View style={styles.planCardNutritionBlock}>
            {scaled_line ? (
              <Text
                style={[
                  styles.planCardScaledLine,
                  { color: colors.textLight, textAlign: 'center', writingDirection },
                  ANDROID_TEXT,
                ]}
                numberOfLines={1}>
                {scaled_line}
              </Text>
            ) : null}
            {calorie_label ? (
              <Text
                style={[
                  styles.planCardCalorieLine,
                  { color: colors.textSecondary, textAlign: 'center', writingDirection },
                  ANDROID_TEXT,
                ]}
                numberOfLines={1}>
                {calorie_label}
              </Text>
            ) : null}
          </View>

          <View style={styles.planCardSelectCol}>{render_select_chip(false)}</View>
        </View>
      ) : (
        <View style={[styles.planCardCompactRow, flipRowStyle]}>
          <View style={styles.planCardCompactCopy}>
            {plain_meta_line ? (
              <Text
                style={[
                  styles.planCardPlainMeta,
                  { color: colors.textSecondary, textAlign, writingDirection },
                  ANDROID_TEXT,
                ]}
                numberOfLines={2}>
                {plain_meta_line}
              </Text>
            ) : null}
          </View>
          <View style={styles.planCardSelectColCompact}>{render_select_chip(true)}</View>
        </View>
      )}
    </>
  );

  return (
    <View style={styles.cardWrap}>
      <Clickable
        onPress={onSelect}
        haptics
        pressScale={PRESS_PRESETS.listRow.scale}
        /** Native pressable avoids RNGH remount double-fires when expandable layout changes mid-tap. */
        useNativePressable={expandable}
        pressAnimated={!expandable}
        style={[styles.planCard, selected && styles.planCardSelected]}
        contentBorderRadius={DASH_STUDIO_RADIUS}
        accessibilityRole="button"
        accessibilityLabel={String(title)}
        accessibilityState={{ selected }}>
        <View style={[styles.planCardInner, selected && styles.planCardInnerSelected]}>
          {selected ? (
            <View style={styles.planCardSelectedBadge} pointerEvents="none">
              <SvgIcon name="check" size={14} color={colors.onPrimary ?? colors.surface} />
            </View>
          ) : null}
          <Animated.View style={[styles.planCardImageBlock, expandable ? image_block_anim : null]}>
            {image_uri ? (
              <Image
                source={{ uri: image_uri }}
                style={styles.planCardImage}
                resizeMode="cover"
                fadeDuration={0}
                onError={on_image_error}
                {...(Platform.OS === 'android' ? { progressiveRenderingEnabled: true } : {})}
              />
            ) : (
              <View style={styles.planCardImagePlaceholder} />
            )}
            <View style={styles.planCardImageBadges}>
              {has_offer ? <OfferBadge label={t('subscriptions.offer')} styles={styles} /> : null}
              <View style={styles.badgePrice}>
                {strike_price != null ? (
                  <Text style={[styles.badgePriceStrike, ANDROID_TEXT]} numberOfLines={1}>
                    {formatCurrency(Number(strike_price))}
                  </Text>
                ) : null}
                <Text style={[styles.badgePriceText, ANDROID_TEXT]} numberOfLines={1}>
                  {formatCurrency(Number(price))}
                </Text>
              </View>
            </View>
          </Animated.View>

          <View style={styles.planCardFooter}>
            {expandable ? (
              <>
                <Text style={[styles.planCardTitle, { textAlign, writingDirection }, ANDROID_TEXT]} numberOfLines={2}>
                  {title}
                </Text>
                <Animated.View style={[styles.planCardCompactRow, flipRowStyle, compact_meta_anim]}>
                  <View style={styles.planCardCompactCopy}>
                    {plain_meta_line ? (
                      <Text
                        style={[
                          styles.planCardPlainMeta,
                          { color: colors.textSecondary, textAlign, writingDirection },
                          ANDROID_TEXT,
                        ]}
                        numberOfLines={2}>
                        {plain_meta_line}
                      </Text>
                    ) : null}
                  </View>
                  {!selected ? (
                    <View style={styles.planCardSelectColCompact}>{render_select_chip(true)}</View>
                  ) : null}
                </Animated.View>
                <Animated.View style={details_anim}>{expanded_details}</Animated.View>
              </>
            ) : show_nutrition ? (
              <>
                <Text style={[styles.planCardTitle, { textAlign, writingDirection }, ANDROID_TEXT]} numberOfLines={2}>
                  {title}
                </Text>
                {expanded_details}
              </>
            ) : (
              <View style={[styles.planCardCompactRow, flipRowStyle]}>
                <View style={styles.planCardCompactCopy}>
                  <Text style={[styles.planCardTitle, { textAlign, writingDirection }, ANDROID_TEXT]} numberOfLines={2}>
                    {title}
                  </Text>
                  {plain_meta_line ? (
                    <Text
                      style={[
                        styles.planCardPlainMeta,
                        { color: colors.textSecondary, textAlign, writingDirection },
                        ANDROID_TEXT,
                      ]}
                      numberOfLines={2}>
                      {plain_meta_line}
                    </Text>
                  ) : null}
                </View>
                <View style={styles.planCardSelectColCompact}>{render_select_chip(true)}</View>
              </View>
            )}
          </View>
        </View>
      </Clickable>
    </View>
  );
});
